/**
 * Markdown to HTML converter for guide popups.
 * Supports: headings, bold, italic, lists, blockquotes, links, code, horizontal rules, images, tables.
 * @param {string} md - Markdown content
 * @param {string} basePath - Base path for relative images (e.g. '01-quy_dinh_bat_buoc')
 */
export function videoEmbed(line) {
  const url = (line.match(/^\[[^\]]*\]\((\S+)\)$/) || [])[1] || (line.match(/^<?(https?:\/\/\S+?)>?$/) || [])[1];
  if (!url) return null;
  const loom = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return null;
}

export function markdownToHtml(md, basePath = '') {
  if (!md) return '';

  // Normalize line endings
  let text = md.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Split into lines for block-level processing
  const lines = text.split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line → skip
    if (line.trim() === '') { i++; continue; }

    // Video: link Loom / YouTube đứng riêng 1 dòng (hoặc [chữ](link)) → khung xem ngay tại chỗ.
    // src dựng lại từ mã video (chỉ chữ-số) → không nhét được HTML lạ vào iframe.
    const video = videoEmbed(line.trim());
    if (video) {
      blocks.push(`<div style="position:relative;padding-bottom:56.25%;height:0;margin:12px 0;border-radius:8px;overflow:hidden;background:#000"><iframe src="${video}" style="position:absolute;inset:0;width:100%;height:100%;border:0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`);
      i++; continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      blocks.push('<hr style="border:none;border-top:1px solid #e5e7eb;margin:12px 0">');
      i++; continue;
    }

    // Headings
    const h4 = line.match(/^#### (.+)$/);
    if (h4) { blocks.push(`<h4 style="font-size:13px;font-weight:700;margin:12px 0 4px;color:#374151">${inlineFormat(h4[1])}</h4>`); i++; continue; }
    const h3 = line.match(/^### (.+)$/);
    if (h3) { blocks.push(`<h3 style="font-size:14px;font-weight:700;margin:14px 0 6px;color:#1f2937">${inlineFormat(h3[1])}</h3>`); i++; continue; }
    const h2 = line.match(/^## (.+)$/);
    if (h2) { blocks.push(`<h2 style="font-size:15px;font-weight:700;margin:16px 0 6px;color:#111827">${inlineFormat(h2[1])}</h2>`); i++; continue; }
    const h1 = line.match(/^# (.+)$/);
    if (h1) { blocks.push(`<h1 style="font-size:17px;font-weight:800;margin:16px 0 8px;color:#111827">${inlineFormat(h1[1], basePath)}</h1>`); i++; continue; }

    // Blockquote (lines starting with >)
    if (/^>\s?/.test(line)) {
      const quoteLines = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      blocks.push(`<blockquote style="margin:8px 0;padding:8px 12px;border-left:3px solid #60a5fa;background:#eff6ff;border-radius:4px;color:#1e40af;font-size:13px;line-height:1.5">${inlineFormat(quoteLines.join('<br>'), basePath)}</blockquote>`);
      continue;
    }

    // Unordered list (- or *)
    if (/^[-*] /.test(line)) {
      const listItems = [];
      while (i < lines.length) {
        const cur = lines[i];
        if (/^[-*] /.test(cur)) {
          listItems.push(cur.replace(/^[-*] /, ''));
          i++;
        } else if (cur.trim() === '') {
          // peek: if next non-blank line is also a list item, skip blank
          let j = i + 1;
          while (j < lines.length && lines[j].trim() === '') j++;
          if (j < lines.length && /^[-*] /.test(lines[j])) { i++; continue; }
          break;
        } else { break; }
      }
      const lis = listItems.map(li => `<li style="margin:3px 0;line-height:1.5">${inlineFormat(li, basePath)}</li>`).join('');
      blocks.push(`<ul style="padding-left:20px;margin:6px 0;list-style:disc">${lis}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\d+\. /.test(line)) {
      const listItems = [];
      while (i < lines.length) {
        const cur = lines[i];
        if (/^\d+\. /.test(cur)) {
          listItems.push(cur.replace(/^\d+\. /, ''));
          i++;
        } else if (cur.trim() === '') {
          let j = i + 1;
          while (j < lines.length && lines[j].trim() === '') j++;
          if (j < lines.length && /^\d+\. /.test(lines[j])) { i++; continue; }
          break;
        } else { break; }
      }
      const lis = listItems.map(li => `<li style="margin:3px 0;line-height:1.5">${inlineFormat(li, basePath)}</li>`).join('');
      blocks.push(`<ol style="padding-left:20px;margin:6px 0;list-style:decimal">${lis}</ol>`);
      continue;
    }

    // Image on its own line: ![alt](url)
    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      const alt = imgMatch[1] || 'Ảnh';
      let src = imgMatch[2];
      // Auto resolve relative path
      if (!src.startsWith('http') && !src.startsWith('/') && basePath) {
        src = `/kienthuc/${basePath}/${src}`;
      } else if (!src.startsWith('http') && !src.startsWith('/')) {
        src = `/kienthuc/${src}`;
      }
      blocks.push(`<figure style="margin:12px 0;text-align:center"><img src="${src}" alt="${alt}" style="max-width:100%;border-radius:8px;border:1px solid #e5e7eb" />${alt ? `<figcaption style="font-size:11px;color:#6b7280;margin-top:4px">${alt}</figcaption>` : ''}</figure>`);
      i++; continue;
    }

    // Table: lines starting with |
    if (/^\|/.test(line)) {
      const tableRows = [];
      while (i < lines.length && /^\|/.test(lines[i])) {
        const row = lines[i].trim();
        // Skip separator rows like |---|---|
        if (/^\|[\s\-:|]+\|$/.test(row)) { i++; continue; }
        const cells = row.split('|').filter((c, idx, arr) => idx > 0 && idx < arr.length - 1).map(c => c.trim());
        tableRows.push(cells);
        i++;
      }
      if (tableRows.length > 0) {
        const headerRow = tableRows[0];
        const bodyRows = tableRows.slice(1);
        let tableHtml = '<table style="width:100%;border-collapse:collapse;margin:8px 0;font-size:12px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden">';
        tableHtml += '<thead><tr>' + headerRow.map(c => `<th style="padding:6px 10px;background:#f8fafc;border:1px solid #e5e7eb;font-weight:600;text-align:left">${inlineFormat(c, basePath)}</th>`).join('') + '</tr></thead>';
        if (bodyRows.length > 0) {
          tableHtml += '<tbody>' + bodyRows.map(row => '<tr>' + row.map(c => `<td style="padding:6px 10px;border:1px solid #e5e7eb">${inlineFormat(c, basePath)}</td>`).join('') + '</tr>').join('') + '</tbody>';
        }
        tableHtml += '</table>';
        blocks.push(tableHtml);
      }
      continue;
    }

    // Paragraph: collect consecutive non-special lines
    const paraLines = [];
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,4} |[-*] |\d+\. |> |---+$|!\[|\|)/.test(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push(`<p style="margin:6px 0;line-height:1.5">${inlineFormat(paraLines.join('<br>'), basePath)}</p>`);
    }
  }

  return blocks.join('');
}

/**
 * Process inline markdown formatting: bold, italic, code, links
 */
function inlineFormat(text, basePath = '') {
  return text
    // Escape HTML (but preserve our <br>)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Restore <br>
    .replace(/&lt;br&gt;/g, '<br>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px;font-size:12px;font-family:monospace">$1</code>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Images ![alt](url) - must come before links
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, src) => {
      let url = src;
      if (!url.startsWith('http') && !url.startsWith('/') && basePath) {
        url = `/kienthuc/${basePath}/${url}`;
      } else if (!url.startsWith('http') && !url.startsWith('/')) {
        url = `/kienthuc/${url}`;
      }
      return `<img src="${url}" alt="${alt}" style="max-width:100%;border-radius:6px;border:1px solid #e5e7eb;margin:4px 0" />`;
    })
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" style="color:#2563eb;text-decoration:underline">$1</a>');
}
